import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../../i18n";
import { EASE } from "./Reveal";

export function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];
  const base = 0.4; // reveal plays independently; preloader is the on-load curtain

  const scrollTo = (sel) => document.querySelector(sel)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] flex items-center overflow-hidden">
      {/* subtle rotating gold ring, decorative */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -right-40 top-1/2 -translate-y-1/2 hidden md:block"
        animate={{ rotate: 360 }}
        transition={{ duration: 90, repeat: Infinity, ease: "linear" }}
      >
        <div className="w-[620px] h-[620px] rounded-full border border-[var(--gold)]/25" />
        <div className="absolute inset-16 rounded-full border border-[var(--gold)]/15" />
      </motion.div>

      <motion.div style={{ y, opacity }} className="relative z-10 mx-auto max-w-[1400px] w-full px-6 md:px-10 pt-28">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: base, ease: EASE }}
          className="eyebrow mb-8"
        >
          {t.hero.eyebrow}
        </motion.p>

        <h1 className="font-display font-light leading-[0.92] tracking-tight text-[13vw] sm:text-[11vw] md:text-[9vw] lg:text-[8vw]">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.05, delay: base + 0.15 + i * 0.13, ease: EASE }}
              >
                {i === 2 ? <em className="italic text-[var(--gold-dark)] font-normal">{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: base + 0.7, ease: EASE }}
          className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <p className="max-w-md text-[var(--ink-2)] text-base md:text-lg leading-relaxed">
            {t.hero.sub}
          </p>
          <button
            data-testid="hero-cta"
            onClick={() => scrollTo("#lookbook")}
            className="group shrink-0 inline-flex items-center gap-3 border border-[var(--ink)]/25 rounded-full pl-7 pr-2 py-2 hover:border-[var(--gold)] transition-colors duration-500"
          >
            <span className="eyebrow text-[var(--ink)]">{t.hero.cta}</span>
            <span className="grid place-items-center w-9 h-9 rounded-full bg-[var(--ink)] text-[var(--bg)] group-hover:bg-[var(--gold-dark)] transition-colors duration-500">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </span>
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: base + 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="eyebrow text-[10px]">{t.hero.scroll}</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-8 bg-[var(--gold)]"
        />
      </motion.div>
    </section>
  );
}
